import { useEffect, useMemo, useState } from 'react';
import { useAccount } from '../contexts/AccountContext';
import { useI18n } from '../i18n/I18nProvider';
import { usePropertyCatalog } from '../hooks/usePropertyCatalog';
import { findPropertyByName, resolvePropertyCode } from '../lib/propertyCatalog';
import { usePortfolioVisitPlans, visitPlansToCalendarTrips } from '../hooks/useVisitPlans';
import {
  currentAndFutureTrips,
  formatTripWindow,
  fourMonthStarts,
  mergeCalendarTrips,
  monthCells,
  parseISODate,
  portfolioCoverageTrips,
  portfolioLegendProperties,
  propertyCalendarColor,
  rangeLabel,
  shiftAnchor,
  todayISO,
  toISODate,
  tripsInRange,
  tripsOnDate,
  visibleRange,
  weekCells,
  withCoverageTones,
  withPropertyCode,
  WEEKDAY_LABELS,
  type CalendarRange,
  type CalendarTrip,
} from '../lib/tripCalendar';

interface PortfolioTripCalendarProps {
  selectedProperty?: string | null;
  onSelectProperty?: (code: string | null) => void;
  /**
   * Limit the catalog trips and the legend to these property codes — the
   * properties in the signed-in user's scope. Omitted = every property.
   */
  propertyCodes?: string[] | null;
}

const VISIT_REASON_FONT = "font-[Georgia,'Iowan_Old_Style','Times_New_Roman',serif] italic font-normal tracking-tight";

function chipClass(trip: CalendarTrip): string {
  const color = propertyCalendarColor(trip.propertyCode);
  if (trip.tone === 'current') {
    return `${color.fill} ${color.text} ring-1 ring-inset ring-current`;
  }
  if (trip.tone === 'past') {
    return `${color.fill} ${color.text} opacity-70`;
  }
  return `${color.fill} ${color.text}`;
}

function VisitChip({
  trip,
  compact,
}: {
  trip: CalendarTrip;
  compact: boolean;
}) {
  const propertyName = trip.propertyCode ?? trip.locationName ?? 'Visit';
  return (
    <span
      title={`${trip.locationName ?? propertyName} — ${trip.title}`}
      className={`block w-full min-w-0 max-w-full overflow-hidden rounded px-1 py-0.5 leading-tight ${chipClass(trip)} ${compact ? 'text-[10px]' : 'text-[10px] sm:text-[11px]'}`}
    >
      <span className="block truncate font-sans font-semibold">{propertyName}</span>
      <span className={`block truncate ${VISIT_REASON_FONT}`}>{trip.title}</span>
    </span>
  );
}

function toneLabel(
  trip: CalendarTrip,
  t: (key: 'property.trips.current' | 'property.trips.upcoming' | 'property.trips.completed') => string,
): string {
  if (trip.tone === 'current') return t('property.trips.current');
  if (trip.tone === 'past') return t('property.trips.completed');
  return t('property.trips.upcoming');
}

export function PortfolioTripCalendar({
  selectedProperty = null,
  onSelectProperty,
  propertyCodes = null,
}: PortfolioTripCalendarProps) {
  const { t } = useI18n();
  const { accounts } = useAccount();
  const customerIds = useMemo(() => accounts.map((account) => account.customer_id), [accounts]);
  const { data: planned = [] } = usePortfolioVisitPlans(customerIds);
  const [filterCode, setFilterCode] = useState<string | null>(selectedProperty);
  const [view, setView] = useState<CalendarRange>('month');
  const [anchor, setAnchor] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => todayISO());

  useEffect(() => {
    if (selectedProperty) setFilterCode(selectedProperty);
  }, [selectedProperty]);

  useEffect(() => {
    const now = new Date();
    if (filterCode) {
      setView('fourMonth');
      setAnchor(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      return;
    }
    setView('month');
    setAnchor(now);
  }, [filterCode]);

  const { catalog } = usePropertyCatalog();

  const scope = useMemo(() => {
    if (!propertyCodes) return null;
    const resolved = propertyCodes
      .map((code) => resolvePropertyCode(catalog, code))
      .filter((code): code is string => !!code);
    return new Set(resolved);
  }, [catalog, propertyCodes]);

  const catalogTrips = useMemo(
    () =>
      portfolioCoverageTrips(catalog, new Date(), filterCode).filter(
        (trip) => !scope || (!!trip.propertyCode && scope.has(trip.propertyCode)),
      ),
    [catalog, filterCode, scope],
  );

  const trips = useMemo(() => {
    const today = new Date();
    const plannedTrips = visitPlansToCalendarTrips(planned).map((trip) => {
      const plan = planned.find((row) => `planned:${row.id}` === trip.id);
      const account = accounts.find((item) => item.customer_id === plan?.customer_id);
      const property = findPropertyByName(catalog, account?.name);
      return withPropertyCode(catalog, trip, property?.code, property?.name ?? account?.name);
    }).filter((trip) => !filterCode || trip.propertyCode === filterCode);
    const merged = mergeCalendarTrips(catalogTrips, plannedTrips);
    return filterCode ? withCoverageTones(merged, today) : currentAndFutureTrips(merged, today);
  }, [accounts, catalog, catalogTrips, filterCode, planned]);

  const legend = useMemo(
    () => portfolioLegendProperties(catalog).filter((item) => !scope || scope.has(item.code)),
    [catalog, scope],
  );
  const selectedMeta = legend.find((item) => item.code === filterCode) ?? null;
  const windowRange = visibleRange(anchor, view);
  const tripsThisWindow = tripsInRange(trips, windowRange.start, windowRange.end);
  const today = todayISO();
  const selectedTrips = tripsOnDate(trips, selectedDate);
  const currentTrips = trips.filter((trip) => trip.tone === 'current');
  const upcomingTrips = trips.filter((trip) => trip.tone === 'upcoming').slice(0, 8);
  const previousTrips = filterCode
    ? trips.filter((trip) => trip.tone === 'past').slice().sort((a, b) => b.end.localeCompare(a.end) || b.start.localeCompare(a.start)).slice(0, 12)
    : [];

  const openProperty = (code: string) => onSelectProperty?.(code);
  const jumpToTrip = (trip: CalendarTrip) => {
    setAnchor(parseISODate(trip.start));
    setSelectedDate(trip.start);
    if (view === 'fourMonth') setView('month');
  };

  return (
    <div className="min-w-0 rounded-lg border border-border bg-surface p-4 sm:p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-sm font-medium text-text-muted">{t('property.trips.portfolio_title')}</h2>
          <p className="mt-0.5 text-xs text-text-faint">
            {selectedMeta
              ? `${selectedMeta.code} · ${selectedMeta.name}`
              : t('property.trips.portfolio_subtitle')}
          </p>
          {selectedMeta ? (
            <p className={`mt-0.5 text-xs text-text-muted ${VISIT_REASON_FONT}`}>
              {t('property.trips.portfolio_history')}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAnchor((current) => shiftAnchor(current, view, -1))}
              className="rounded-md border border-border px-2 py-1 text-sm text-text-muted hover:bg-surface-strong"
              aria-label={t('calendar.previous')}
            >
              ‹
            </button>
            <p className="min-w-[9rem] text-sm font-medium text-text">{rangeLabel(anchor, view)}</p>
            <button
              type="button"
              onClick={() => setAnchor((current) => shiftAnchor(current, view, 1))}
              className="rounded-md border border-border px-2 py-1 text-sm text-text-muted hover:bg-surface-strong"
              aria-label={t('calendar.next')}
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                setAnchor(now);
                setSelectedDate(todayISO(now));
              }}
              className="rounded-md border border-border px-2.5 py-1 text-xs text-text-muted hover:bg-surface-strong"
            >
              {t('property.trips.today')}
            </button>
          </div>
          <div className="flex rounded-md border border-border p-0.5">
            {([
              ['week', t('property.trips.week')],
              ['month', t('property.trips.month')],
              ['fourMonth', t('property.trips.four_month')],
            ] as const).map(([option, label]) => (
              <button
                key={option}
                type="button"
                onClick={() => setView(option)}
                className={`rounded px-2.5 py-1 text-xs font-medium ${
                  view === option ? 'bg-surface-3 text-text' : 'text-text-muted hover:text-text'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {legend.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {legend.map((item) => {
            const color = propertyCalendarColor(item.code);
            const active = filterCode === item.code;
            return (
              <button
                key={item.code}
                type="button"
                onClick={() => {
                  const next = active ? null : item.code;
                  setFilterCode(next);
                  onSelectProperty?.(next);
                }}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] font-medium ${
                  active ? 'border-primary bg-primary/10 text-text' : 'border-border text-text-muted hover:bg-surface-strong'
                }`}
                title={item.name}
              >
                <span className={`h-2 w-2 rounded-full ${color.swatch}`} />
                {item.code}
                <span className="text-text-faint">{item.count}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-text-faint">
        <span>
          <span className="mr-1 inline-block h-2 w-2 rounded-full bg-primary align-middle" />
          {t('property.trips.current')} · {currentTrips.length}
        </span>
        <span>
          <span className="mr-1 inline-block h-2 w-2 rounded-full bg-primary/40 align-middle" />
          {t('property.trips.upcoming')} · {trips.filter((trip) => trip.tone === 'upcoming').length}
        </span>
        {filterCode ? (
          <span>
            {t('property.trips.previous')} · {trips.filter((trip) => trip.tone === 'past').length}
          </span>
        ) : null}
        <span>{tripsThisWindow.length} in this window</span>
      </div>

      <div className="mt-3 grid min-w-0 grid-cols-1 items-start gap-4 2xl:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="min-w-0">
          {view === 'week' ? (
            <WeekGrid
              anchor={anchor}
              trips={trips}
              selectedDate={selectedDate}
              today={today}
              onSelect={setSelectedDate}
            />
          ) : null}
          {view === 'month' ? (
            <MonthGrid
              year={anchor.getFullYear()}
              month={anchor.getMonth()}
              trips={trips}
              selectedDate={selectedDate}
              today={today}
              onSelect={setSelectedDate}
            />
          ) : null}
          {view === 'fourMonth' ? (
            <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
              {fourMonthStarts(anchor).map((monthStart) => (
                <div key={toISODate(monthStart)} className="min-w-0 overflow-hidden rounded-md border border-border/70 p-2 sm:p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">
                    {monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                  <MonthGrid
                    year={monthStart.getFullYear()}
                    month={monthStart.getMonth()}
                    trips={trips}
                    selectedDate={selectedDate}
                    today={today}
                    compact
                    onSelect={setSelectedDate}
                  />
                </div>
              ))}
            </div>
          ) : null}

          <div className="mt-4 rounded-md border border-border bg-surface-strong/40 px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">
              {formatTripWindow(selectedDate, selectedDate)}
            </p>
            {selectedTrips.length === 0 ? (
              <p className="mt-2 text-sm text-text-faint">
                {filterCode ? t('property.trips.empty_day') : t('property.trips.empty_coverage')}
              </p>
            ) : (
              <ul className="mt-2 space-y-2">
                {selectedTrips.map((trip) => (
                  <li key={trip.id}>
                    <button
                      type="button"
                      onClick={() => trip.propertyCode && openProperty(trip.propertyCode)}
                      className="w-full text-left"
                    >
                      <p className="flex items-center gap-2 text-sm font-medium text-text">
                        <span className={`h-2.5 w-2.5 rounded-full ${propertyCalendarColor(trip.propertyCode).swatch}`} />
                        {trip.locationName ?? trip.propertyCode ?? 'Visit'}
                      </p>
                      <p className={`mt-0.5 pl-4 text-sm text-text-muted ${VISIT_REASON_FONT}`}>{trip.title}</p>
                      <p className="mt-0.5 pl-4 text-xs text-text-faint">
                        {formatTripWindow(trip.start, trip.end)}
                        {' · '}
                        {toneLabel(trip, t)}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <aside className="min-w-0 self-start rounded-md border border-border p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">
            {t('property.trips.coverage_rail')}
          </p>
          {currentTrips.length > 0 && (
            <div className="mt-2 space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-success">{t('property.trips.current')}</p>
              {currentTrips.map((trip) => (
                <CoverageRow key={trip.id} trip={trip} onSelectProperty={openProperty} />
              ))}
            </div>
          )}
          <div className="mt-3 space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-faint">{t('property.trips.upcoming')}</p>
            {upcomingTrips.length === 0 ? (
              <p className="text-xs text-text-faint">{t('property.trips.empty_coverage')}</p>
            ) : (
              upcomingTrips.map((trip) => (
                <CoverageRow key={trip.id} trip={trip} onSelectProperty={openProperty} />
              ))
            )}
          </div>
          {filterCode ? (
            <div className="mt-3 space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-faint">{t('property.trips.previous')}</p>
              {previousTrips.length === 0 ? (
                <p className="text-xs text-text-faint">{t('property.trips.empty_day')}</p>
              ) : (
                previousTrips.map((trip) => (
                  <CoverageRow
                    key={trip.id}
                    trip={trip}
                    onSelectProperty={openProperty}
                    onJump={() => jumpToTrip(trip)}
                  />
                ))
              )}
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

function CoverageRow({
  trip,
  onSelectProperty,
  onJump,
}: {
  trip: CalendarTrip;
  onSelectProperty?: (code: string) => void;
  onJump?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        if (onJump) {
          onJump();
          return;
        }
        if (trip.propertyCode) onSelectProperty?.(trip.propertyCode);
      }}
      className="w-full rounded-md px-1 py-1 text-left hover:bg-surface-strong"
    >
      <p className="flex items-center gap-1.5 text-xs font-semibold text-text">
        <span className={`h-2 w-2 rounded-full ${propertyCalendarColor(trip.propertyCode).swatch}`} />
        {trip.locationName ?? trip.propertyCode ?? 'Trip'}
      </p>
      <p className={`truncate pl-3.5 text-[12px] text-text-muted ${VISIT_REASON_FONT}`} title={trip.title}>
        {trip.title}
      </p>
      <p className="pl-3.5 text-[10px] text-text-faint">{formatTripWindow(trip.start, trip.end)}</p>
    </button>
  );
}

function WeekdayHeader() {
  return (
    <div className="grid min-w-0 grid-cols-7 gap-px">
      {WEEKDAY_LABELS.map((label) => (
        <div key={label} className="px-1 py-1 text-center text-[10px] font-semibold uppercase tracking-[0.08em] text-text-faint">
          {label}
        </div>
      ))}
    </div>
  );
}

function MonthGrid({
  year,
  month,
  trips,
  selectedDate,
  today,
  compact = false,
  onSelect,
}: {
  year: number;
  month: number;
  trips: CalendarTrip[];
  selectedDate: string;
  today: string;
  compact?: boolean;
  onSelect: (iso: string) => void;
}) {
  const cells = monthCells(year, month);
  const visibleCount = compact ? 1 : 2;
  return (
    <div>
      <WeekdayHeader />
      <div className="grid min-w-0 grid-cols-7 gap-px rounded-md bg-border">
        {cells.map((date) => {
          const iso = toISODate(date);
          const inMonth = date.getMonth() === month;
          const dayTrips = tripsOnDate(trips, iso);
          const extra = Math.max(0, dayTrips.length - visibleCount);
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onSelect(iso)}
              aria-pressed={iso === selectedDate}
              aria-current={iso === today ? 'date' : undefined}
              className={`min-w-0 overflow-hidden min-h-[2.75rem] ${compact ? 'sm:min-h-[5.5rem]' : 'sm:min-h-[7.5rem]'} bg-surface p-1 text-left hover:bg-surface-strong ${
                iso === selectedDate ? 'ring-1 ring-inset ring-primary' : ''
              } ${iso === today ? 'bg-primary/5' : ''} ${inMonth ? '' : 'opacity-40'}`}
            >
              <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                iso === today ? 'bg-primary text-white' : 'text-text-muted'
              }`}>
                {date.getDate()}
              </span>
              {/* A month cell is ~44px wide on a phone. Below sm each visit is a
                  dot in its property colour; the day panel underneath names
                  them. From sm up the labelled chips return. */}
              <span className="mt-1 flex flex-wrap gap-0.5 sm:hidden" aria-hidden="true">
                {dayTrips.slice(0, 3).map((trip) => (
                  <span
                    key={trip.id}
                    className={`h-1.5 w-1.5 rounded-full ${propertyCalendarColor(trip.propertyCode).swatch}`}
                  />
                ))}
              </span>
              {dayTrips.length > 0 && (
                <span className="sr-only">
                  {dayTrips.length === 1
                    ? `${dayTrips[0].locationName ?? dayTrips[0].propertyCode ?? ''} ${dayTrips[0].title}`
                    : `${dayTrips.length}`}
                </span>
              )}
              <span className="mt-0.5 hidden space-y-0.5 sm:block">
                {dayTrips.slice(0, visibleCount).map((trip) => (
                  <VisitChip key={trip.id} trip={trip} compact={compact} />
                ))}
                {extra > 0 && <span className="block px-1 text-[10px] text-text-faint">+{extra}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WeekGrid({
  anchor,
  trips,
  selectedDate,
  today,
  onSelect,
}: {
  anchor: Date;
  trips: CalendarTrip[];
  selectedDate: string;
  today: string;
  onSelect: (iso: string) => void;
}) {
  const days = weekCells(anchor);
  return (
    <div>
      <WeekdayHeader />
      <div className="grid min-w-0 grid-cols-1 gap-px rounded-md bg-border sm:grid-cols-7">
        {days.map((date) => {
          const iso = toISODate(date);
          const dayTrips = tripsOnDate(trips, iso);
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onSelect(iso)}
              className={`min-h-[10rem] min-w-0 overflow-hidden bg-surface p-2 text-left hover:bg-surface-strong ${
                iso === selectedDate ? 'ring-1 ring-inset ring-primary' : ''
              } ${iso === today ? 'bg-primary/5' : ''}`}
            >
              <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                iso === today ? 'bg-primary text-white' : 'text-text'
              }`}>
                {date.getDate()}
              </span>
              <span className="mt-2 block space-y-1">
                {dayTrips.map((trip) => (
                  <VisitChip key={trip.id} trip={trip} compact={false} />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
